import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { translateStatus, translateRisk } from "../../lib/constants";

// Category labels and colors
const CATEGORY_CONFIG = {
  competitor: { label: "Concorrente", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  influencer: { label: "Influencer", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  ipss: { label: "IPSS", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  other: { label: "Outro", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" }
};

// Others (KYU) List Page Component
export const OthersPage = () => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [newEntity, setNewEntity] = useState({
    name: '',
    category: 'other',
    tax_id: '',
    description: '',
    relationship_type: '',
    address: '',
    city: '',
    website: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchEntities = async () => {
    try {
      let params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (filterCategory) params.push(`category=${filterCategory}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const response = await api.get(`/others${queryString}`);
      setEntities(response.data);
    } catch (error) {
      console.error('Error fetching others:', error);
      toast.error('Erro ao carregar entidades KYU');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, [search, filterCategory]);

  const handleCreate = async () => {
    if (!newEntity.name) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      await api.post('/others', newEntity);
      toast.success('Entidade KYU criada com sucesso');
      setShowCreateModal(false);
      setNewEntity({
        name: '',
        category: 'other',
        tax_id: '',
        description: '',
        relationship_type: '',
        address: '',
        city: '',
        website: '',
        contact_name: '',
        contact_email: '',
        contact_phone: ''
      });
      fetchEntities();
    } catch (error) {
      console.error('Error creating entity:', error);
      toast.error('Erro ao criar entidade');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryBadge = (category) => {
    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div data-testid="others-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Outros (KYU)</h1>
          <p className="text-muted-foreground">Know Your Universe - Concorrentes, Influencers, IPSS e outros</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="create-other-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Adicionar
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Pesquisar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="search-input"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 rounded-lg border border-input bg-background"
          data-testid="category-filter"
        >
          <option value="">Todas as categorias</option>
          <option value="competitor">Concorrentes</option>
          <option value="influencer">Influencers</option>
          <option value="ipss">IPSS</option>
          <option value="other">Outros</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
          const count = entities.filter(e => e.category === key).length;
          return (
            <div key={key} className="p-4 rounded-xl border bg-card">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${config.color.split(' ')[0]}`}></span>
                <span className="text-sm text-muted-foreground">{config.label}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : entities.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          <p className="text-muted-foreground">Nenhuma entidade KYU registada</p>
          <button onClick={() => setShowCreateModal(true)} className="mt-4 text-primary hover:underline">
            Criar o primeiro registo
          </button>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Categoria</th>
                <th>NIF</th>
                <th>Cidade</th>
                <th>Status</th>
                <th>Risco</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entities.map((entity) => (
                <tr 
                  key={entity.entity_id} 
                  className="cursor-pointer hover:bg-muted/50 transition-colors" 
                  onClick={() => navigate(`/others/${entity.entity_id}`)}
                  data-testid={`entity-row-${entity.entity_id}`}
                >
                  <td className="font-medium">
                    <div>
                      <p>{entity.name}</p>
                      {entity.description && (
                        <p className="text-xs text-muted-foreground truncate max-w-xs">{entity.description}</p>
                      )}
                    </div>
                  </td>
                  <td>{getCategoryBadge(entity.category)}</td>
                  <td className="text-muted-foreground">{entity.tax_id || '-'}</td>
                  <td className="text-muted-foreground">{entity.city || '-'}</td>
                  <td>
                    <span className={`status-badge status-badge-${entity.status || 'active'}`}>
                      {translateStatus(entity.status || 'active')}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-badge-${entity.risk_level || 'low'}`}>
                      {translateRisk(entity.risk_level || 'low')}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="p-1 text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); navigate(`/others/${entity.entity_id}`); }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold">Nova Entidade KYU</h3>
              <p className="text-sm text-muted-foreground">Know Your Universe - Adicionar nova entidade a monitorizar</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome *</label>
                <input
                  type="text"
                  value={newEntity.name}
                  onChange={(e) => setNewEntity({ ...newEntity, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="Nome da entidade"
                  data-testid="entity-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categoria *</label>
                <select
                  value={newEntity.category}
                  onChange={(e) => setNewEntity({ ...newEntity, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  data-testid="entity-category-select"
                >
                  <option value="competitor">Concorrente</option>
                  <option value="influencer">Influencer</option>
                  <option value="ipss">IPSS</option>
                  <option value="other">Outro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">NIF</label>
                <input
                  type="text"
                  value={newEntity.tax_id}
                  onChange={(e) => setNewEntity({ ...newEntity, tax_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="Número de Identificação Fiscal"
                  data-testid="entity-taxid-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição</label>
                <textarea
                  value={newEntity.description}
                  onChange={(e) => setNewEntity({ ...newEntity, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background min-h-[80px]"
                  placeholder="Descrição da entidade e razão para monitorização"
                  data-testid="entity-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Relação</label>
                <input
                  type="text"
                  value={newEntity.relationship_type}
                  onChange={(e) => setNewEntity({ ...newEntity, relationship_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="Ex: Concorrente direto, Parceiro de caridade, etc."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Morada</label>
                  <input
                    type="text"
                    value={newEntity.address}
                    onChange={(e) => setNewEntity({ ...newEntity, address: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cidade</label>
                  <input
                    type="text"
                    value={newEntity.city}
                    onChange={(e) => setNewEntity({ ...newEntity, city: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input
                  type="url"
                  value={newEntity.website}
                  onChange={(e) => setNewEntity({ ...newEntity, website: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="https://..."
                />
              </div>
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3">Contacto Principal</h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newEntity.contact_name}
                    onChange={(e) => setNewEntity({ ...newEntity, contact_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="Nome do contacto"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="email"
                      value={newEntity.contact_email}
                      onChange={(e) => setNewEntity({ ...newEntity, contact_email: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                      placeholder="Email"
                    />
                    <input
                      type="tel"
                      value={newEntity.contact_phone}
                      onChange={(e) => setNewEntity({ ...newEntity, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                      placeholder="Telefone"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newEntity.name || saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="save-entity-btn"
                >
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OthersPage;
